import _ from "lodash";
import update from "immutability-helper";
import { generateRandomId } from "Utils";
import client from "lib/apolloClient/client";

import {
  ADDING_TO_UNIT,
  ANALYSING,
  GENERATING,
  STREAMING,
  THINKING,
  GATHERING_CURRICULUM_INFORMATION,
  SEARCHING,
  ADDING_TO_UNIT_FLOW,
} from "Constants/stringConstants";
import { getAIResponse, resetAIConversation } from "modules/Services";
import {
  getAIConversationsFromCache,
  writeAIConversationsInCache,
} from "./UnitPlanAIGraphqlHelpers";
import { getUserBasicDetailsFromCache } from "modules/CommonGraphqlHelpers";
import {
  checkIsThinkingState,
  getContentToSend,
  getInputForAIResponse,
  getContextFieldValue,
  getDefaultContextFields,
  getPromptFieldsFromMetadata,
  getHasUserFacingContent,
  getIsUnitInterdisciplinary,
  getHierarchyOfField,
  safeToolArgumentsJSONParse,
} from "./Utils";
import {
  createAIMessageMutation,
  createBulkAiMessagesMutation,
  deleteAIMessageMutation,
} from "./UnitPlanAIMutations";
import { getAIConversationsQuery } from "./UnitPlanAIQueries";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getUnitPlanCentralRepoInputAndFilters } from "Courses/modules/utils";
import { getUnitPlanFieldResponseFilter } from "UnitPlans/modules/Utils";
import { getUnitPlanDetailsFromCache } from "IBPlanner/modules/IBGraphqlHelpers";
import { getSelectedAcademicYearId } from "modules/CommonSelectors";
import { getPlannerElementLabel } from "Assessments/modules/Utils";
import {
  getLearningCourseVariables,
  getSelectedLearningCourseId,
} from "LearningCourse/modules/Utils";
import { getLearningCourseDetailsFromCache } from "LearningCourse/modules/LearningCourseGraphqlHelpers";
import {
  getIsCourseBasedGradingEnabled,
  getIsLearningCoursesEnabled,
} from "LearningCourses/modules/Utils";
import { isSchoolLibraryRoute } from "SchoolLibrary/modules/Utils";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";

const TOOL_NAME_STATE_MAPPING = {
  get_planner_element_list: GATHERING_CURRICULUM_INFORMATION,
  set_template_fields: ADDING_TO_UNIT,
  search: SEARCHING,
  commit_final_unit_flow_to_unit_plan: ADDING_TO_UNIT_FLOW,
};

const ILLEGAL_PATTERNS = [
  ".get_planner_element_list",
  ".set_template_fields",
  ".commit_final_unit_flow_to_unit_plan",
];
const MULTI_TOOL_CALLS_ISSUE_PATTERN = ["multi_tool_use"];

export let cancelRequest;

export const createBulkAiMessages = input => {
  return async dispatch => {
    try {
      return await client.mutate({
        mutation: createBulkAiMessagesMutation,
        variables: { input },
        update: (
          cache,
          {
            data: {
              ai: { createBulkAiMessages },
            },
          }
        ) => {
          cache.updateQuery(
            {
              query: getAIConversationsQuery,
              variables: {
                id: _.get(input, "conversationId"),
              },
            },
            data => {
              const nodes = _.map(createBulkAiMessages?.response, node => {
                return { node, __typename: "AiMessageEdge" };
              }).reverse();

              if (!_.isEmpty(nodes)) {
                // Filter out existing edges where node.id starts with "NEW_"
                const existingEdges = _.get(data, "node.messages.edges", []);
                const filteredEdges = _.filter(
                  existingEdges,
                  edge => !_.startsWith(_.get(edge, "node.id", ""), "NEW_")
                );

                const updatedMessages = [...nodes, ...filteredEdges];
                const updatedData = update(data, {
                  node: {
                    messages: {
                      edges: {
                        $set: updatedMessages,
                      },
                      totalCount: {
                        $set: filteredEdges.length + _.size(nodes),
                      },
                    },
                  },
                });

                return updatedData;
              }

              return data;
            }
          );
        },
      });
    } catch (err) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return {};
    }
  };
};

const getGradesForMetadata = () => {
  return (dispatch, getState) => {
    const state = getState();

    const isCourseBasedGrading = getIsCourseBasedGradingEnabled();
    const academicYearId = getSelectedAcademicYearId(state);

    const learningCourseId = getSelectedLearningCourseId(state);
    const learningCourseVariables = getLearningCourseVariables({
      learningCourseId,
      academicYearId,
    });
    let learningCourse = getLearningCourseDetailsFromCache(
      learningCourseVariables
    );

    const courseId = _.get(state, "teacher.selected_class.selected_course");
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const userInfo = getUserInfo();

    const { centralRepoInput } = getUnitPlanCentralRepoInputAndFilters({
      organizationId: userInfo.org_id,
      courseId,
      academicYearId,
    });
    const responseFilter = getUnitPlanFieldResponseFilter();
    const collaboratorsFilter = {
      userAcademicYearId: academicYearId,
    };

    const unitPlanDetails = getUnitPlanDetailsFromCache({
      unitPlanId,
      centralRepoInput,
      responseFilter,
      collaboratorsFilter,
      exceptionalCollaboratorsFilter: collaboratorsFilter,
    });

    if (isCourseBasedGrading && isSchoolLibraryRoute()) {
      learningCourse = unitPlanDetails?.learningCourse;
    }

    const courseGradeFilters = {
      academicYearId,
    };

    const course = getCourseDetailsFromCache({
      id: courseId,
      courseGradeFilters,
    });

    const value =
      _.get(course?.primaryGrade, "id") ||
      _.get(learningCourse?.primaryGrade, "id") ||
      _.map(course?.grades, "id") ||
      _.map(unitPlanDetails?.grades, "id");

    return _.isArray(value) ? value : [value];
  };
};

export const createAIMessage = ({ variables, writeToCache = true }) => {
  return async dispatch => {
    const userInfo = getUserInfo();

    const conversation = getAIConversationsFromCache({
      id: _.get(variables, "input.conversationId"),
    });

    const newCreateAiMessage = {
      isSuccess: true,
      response: {
        id: generateRandomId(),
        role: null,
        authorType: _.get(variables, "input.authorType"),
        content: _.get(variables, "input.content"),
        metadata: _.get(variables, "input.metadata"),
        createdBy: {
          ...getUserBasicDetailsFromCache({
            id: userInfo.id,
            type: userInfo.userEntityType,
          }),
          type: userInfo.user_type,
          schoolTenures: [],
        },
        votes: {
          myVote: null,
          __typename: "VoteConnection",
        },
        usageMetricsId: null,
        parentMessage: {
          id: null,
          __typename: "AiMessage",
        },
        __typename: "AiMessage",
      },
      __typename: "GenericAiMessageResponse",
    };

    try {
      return await client.mutate({
        mutation: createAIMessageMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          ai: {
            __typename: "AiMutations",
            createAiMessage: newCreateAiMessage,
          },
        },
        update: (
          cache,
          {
            data: {
              ai: { createAiMessage },
            },
          }
        ) => {
          if (writeToCache) {
            const createAiMessageNode = {
              node: _.get(createAiMessage, "response"),
              __typename: "AiMessageEdge",
            };

            const dataToSend = update(conversation, {
              messages: {
                edges: {
                  $unshift: [createAiMessageNode],
                },
                totalCount: {
                  $set: _.get(conversation, "messages.totalCount", 0) + 1,
                },
              },
            });

            writeAIConversationsInCache({
              variables: {
                id: _.get(variables, "input.conversationId"),
              },
              data: {
                node: dataToSend,
              },
            });
          }
        },
      });
    } catch (e) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const onSendQuery = params => {
  const {
    t,
    query,
    contentToStore,
    aiConversationId,
    selectedAction,
    setSelectedAction,
    setCurrentAIState: _setCurrentAIState,
    lastMessageId,
    entityType,
    promptFields,
    sendButtonParams,
    setSendButtonParams,
    unitPlanId,
    curriculumProgramId,
    isUnitFlow,
    unitPlanGrades,
    unitPlanSubjects,
    courseSubjectIds,
    isButtonClicked,
    conversationAttachments,
    fieldLocaleMapping,
    fieldTypeMapping,
    updateUnitFieldsUsingToolResponse,
    parentMessageId: _parentMessageId = null,
    isRetry = false,
    isLastRetry = false,
    setIsStopButtonDisabled,
    setShouldCancelRequest,
    parallelToolCalls,
  } = params;

  const selectedActionClone = _.cloneDeep(selectedAction);
  const promptMetadata = _.get(selectedActionClone, "metadata", {});

  const currentParentMessageId = lastMessageId;
  let parentMessageId = _parentMessageId,
    previousAIState = null;

  const setCurrentAIState = state => {
    previousAIState = state;
    _setCurrentAIState(state);
  };

  return async (dispatch, getState) => {
    const state = getState();

    const isCourseBasedGrading = getIsCourseBasedGradingEnabled();
    const gradesForMetadata = dispatch(getGradesForMetadata());
    const learningCourseId = getSelectedLearningCourseId(state);

    try {
      const isEnglishLanguageSelected = true;

      const content = getContentToSend({
        t,
        selectedAction,
        isEnglishLanguageSelected,
        query,
      });

      if (_.isEmpty(content)) return;

      const userMessageMetadata = {
        isUserInputMessage: _.isEmpty(selectedAction),
      };

      if (
        _.get(selectedAction, "createUserMessage", true) &&
        _.get(sendButtonParams, "createUserMessage", true) &&
        !isRetry
      ) {
        const userMessageContent = _.isEmpty(contentToStore)
          ? content
          : contentToStore;

        // This is required
        const userMessageResponse = await dispatch(
          createAIMessage({
            variables: {
              input: {
                conversationId: aiConversationId,
                authorType: "user",
                content: _.trim(userMessageContent),
                metadata: userMessageMetadata,
                parentMessageId: currentParentMessageId,
              },
            },
          })
        );

        parentMessageId = _.get(
          userMessageResponse,
          "data.ai.createAiMessage.response.id",
          null
        );
      }

      // 3. show ANALYSING... animation
      setCurrentAIState(ANALYSING);

      // 4. generate AI response
      const conversation = getAIConversationsFromCache({
        id: aiConversationId,
      });

      const id = generateRandomId();

      const savedToolUseIds = [];

      // to track mutations from the streaming phase
      const messagesInput = [];
      const toolCallNames = [];

      let responseContent = {},
        previousContent;

      let isIllegalContentFound = false,
        hasMultiToolCallsIssue = false;

      const response = await dispatch(
        getAIResponse({
          useStreamParserV2: true,
          variables: {
            input: getInputForAIResponse({
              selectedAction,
              entityType,
              aiConversationId,
              content: content ?? "",
              unitPlanId,
              curriculumProgramId,
              isUnitFlow,
              unitPlanGrades,
              unitPlanSubjects,
              courseSubjectIds,
              isButtonClicked,
              conversationAttachments,
              fieldLocaleMapping,
              fieldTypeMapping,
              isCourseBasedGrading,
              gradesForMetadata,
              learningCourseId,
              parallelToolCalls,
              schoolInstructions: params.schoolInstructions,
            }),
          },
          getCancelToken: token => {
            setIsStopButtonDisabled(false);
            cancelRequest = token;
          },
          // onDownloadProgress is required for streaming
          onDownloadProgress: params => {
            const {
              fieldType,
              content,
              usageMetricsId,
              userInputFlagged,
              model,
              temperature,
              intentUsageMetricsId,
              metadata,
              languageCode,
              tools,
              latestChunkStatus,
              toolUseResponse: toolUseResponses,
              toolResponseMessage: toolResponseMessages,
              parsedMessages,
            } = params || {};

            // when user cancels the request, responseContent will contain the response generated till that point
            responseContent = { ...params, isCancelled: true };

            const _content = userInputFlagged
              ? t("common:user_input_flagged_msg")
              : content;

            const createAiMessageNode = {
              node: {
                id,
                role: null,
                authorType: "assistant",
                content: _content,
                metadata: {
                  fieldType,
                  userInputFlagged,
                  model,
                  temperature,
                  intentUsageMetricsId,
                  metadata,
                  language: languageCode || "en",
                },
                createdBy: null,
                votes: {
                  myVote: null,
                  __typename: "VoteConnection",
                },
                usageMetricsId: usageMetricsId || null,
                parentMessage: {
                  id: null,
                  __typename: "AiMessage",
                },
                __typename: "AiMessage",
              },
              __typename: "AiMessageEdge",
            };

            const dataToSend = update(conversation, {
              messages: {
                edges: {
                  $unshift: [createAiMessageNode],
                },
                totalCount: {
                  $set: _.get(conversation, "messages.totalCount", 0) + 1,
                },
              },
            });

            if (latestChunkStatus === "TOOL_INDICATION") {
              const latestToolCall = _.chain(params)
                .get("toolCalls")
                .first()
                .get("function.name")
                .value();

              if (!_.includes(toolCallNames, latestToolCall)) {
                toolCallNames.push(latestToolCall);
              }

              const newState = _.get(
                TOOL_NAME_STATE_MAPPING,
                latestToolCall,
                GENERATING
              );

              setCurrentAIState(newState);
              return;
            }

            if (!_.isEmpty(tools) && _.isEmpty(content)) {
              setCurrentAIState(GENERATING);
              return;
            }

            if (!_.isEmpty(_content)) {
              let hasIllegalContent = _.some(ILLEGAL_PATTERNS, pattern =>
                _.includes(_content, pattern)
              );

              hasMultiToolCallsIssue = _.some(
                MULTI_TOOL_CALLS_ISSUE_PATTERN,
                pattern => _.includes(_content, pattern)
              );

              hasIllegalContent = hasIllegalContent || hasMultiToolCallsIssue;

              if (hasIllegalContent) {
                const hasUserFacingContent = getHasUserFacingContent({
                  output: _content,
                  toolCalls: toolCallNames,
                });

                if (!hasUserFacingContent && !isLastRetry) {
                  isIllegalContentFound = true;
                  setShouldCancelRequest(true);
                }
              }

              writeAIConversationsInCache({
                variables: {
                  id: aiConversationId,
                },
                data: {
                  node: dataToSend,
                },
              });

              if (checkIsThinkingState([createAiMessageNode])) {
                setCurrentAIState(THINKING);
              } else {
                const isContentChanged = previousContent !== _content;

                // after tool calls, state is set to streaming for a sec
                // which makes the intermediate state flicker
                // so set it to writing for once
                switch (previousAIState) {
                  case GATHERING_CURRICULUM_INFORMATION:
                  case ADDING_TO_UNIT:
                    setCurrentAIState(GENERATING);
                    break;

                  case GENERATING: {
                    if (isContentChanged) {
                      setCurrentAIState(STREAMING);
                    }
                    break;
                  }

                  default:
                    setCurrentAIState(STREAMING);
                }

                previousContent = _content;
              }
            }

            // create message with empty content with toolResponse
            // so that it shows up in debug mode
            if (!_.isEmpty(parsedMessages)) {
              const toolUseIds = _.chain(parsedMessages)
                .map("message.toolCallId")
                .compact()
                .value();

              // checking if previous toolIds are different from current ones
              // to see if any new tool call is made
              if (!_.isEqual(toolUseIds, savedToolUseIds)) {
                const flatMessages = _.chain(parsedMessages)
                  .filter(item => _.has(item, "message"))
                  .map("message")
                  .value();

                const newToolUseIds = _.difference(toolUseIds, savedToolUseIds);

                if (_.size(newToolUseIds) > 0) {
                  // Find out assistant message(s) which have tool calls for these new toolUseIds
                  const messagesWithToolCalls = _.filter(flatMessages, item => {
                    const toolCallIds = _.map(item?.toolCalls, "id");
                    return (
                      _.intersection(toolCallIds, newToolUseIds).length > 0
                    );
                  });

                  // iterate over each assistant message with tool calls
                  _.forEach(messagesWithToolCalls, messageWithToolCalls => {
                    const toolCalls = messageWithToolCalls?.toolCalls || [];
                    const toolCallIds = _.map(toolCalls, "id");

                    // Find tool responses corresponding to the toolCall Ids of this assistant message
                    const toolResponsesForMessage = _.filter(
                      flatMessages,
                      item => {
                        const { role, toolCallId } = item || {};
                        const isTool = role === "tool";
                        return isTool && _.includes(toolCallIds, toolCallId);
                      }
                    );

                    // push current assistant message
                    messagesInput.push({
                      authorType: "assistant",
                      content: _.get(messageWithToolCalls, "content", ""),
                      usageMetricsId,
                      metadata: {
                        toolCalls: _.get(messageWithToolCalls, "toolCalls", []),
                      },
                      parentMessageId,
                    });

                    // process & push all tool responses for current assistant message
                    _.forEach(
                      toolResponsesForMessage,
                      toolResponseParsedMessage => {
                        const rawToolUseResponse = _.get(
                          toolResponseParsedMessage,
                          "toolResponse",
                          "{}"
                        );
                        const parsedData = safeToolArgumentsJSONParse(
                          rawToolUseResponse
                        );
                        const fieldsToUpdate = _.get(
                          parsedData,
                          "fieldData",
                          []
                        );

                        if (
                          !_.isEmpty(fieldsToUpdate) &&
                          _.isArray(fieldsToUpdate)
                        ) {
                          updateUnitFieldsUsingToolResponse({
                            fields: fieldsToUpdate,
                          });
                        }
                        const toolCallId =
                          toolResponseParsedMessage?.toolCallId;

                        if (!_.isEmpty(toolCallId)) {
                          messagesInput.push({
                            authorType: "tool",
                            content: toolResponseParsedMessage?.toolResponse,
                            metadata: {
                              toolCallId: toolCallId,
                            },
                            parentMessageId,
                          });
                        }
                        // add toolUseId to savedToolUseIds
                        if (!_.includes(savedToolUseIds, toolCallId)) {
                          savedToolUseIds.push(toolCallId);
                        }
                      }
                    );
                  });
                }
              }
            }
          },
        })
      );

      // 5. hide "Stop generation" button and reset local states
      setCurrentAIState("");
      setSelectedAction({});
      setSendButtonParams({});

      // 6. create a message of the generated response from AI
      const {
        content: aiResponse,
        fieldType,
        usageMetricsId,
        model,
        temperature,
        intentUsageMetricsId,
        userInputFlagged = false,
        metadata,
        languageCode,
        isCancelled,
        parsedMessages,
      } = response || responseContent || {};

      const isRequestCancelled = isCancelled || !response;

      const hasUserFacingContent = !isRequestCancelled
        ? getHasUserFacingContent({
            output: aiResponse,
            toolCalls: toolCallNames,
          })
        : true;

      if (!hasUserFacingContent || isIllegalContentFound) {
        return {
          isSuccess: false,
          parentMessageId,
          hasMultiToolCallsIssue,
        };
      }

      if (!_.isEmpty(aiResponse) || userInputFlagged) {
        const content = userInputFlagged
          ? t("common:user_input_flagged_msg")
          : _.get(_.last(parsedMessages), "message.content", null) ||
            aiResponse;

        const isDraftTaskMessage = false;

        // sometimes after calling tools, content is still empty
        // in that case, BE continues the request after tool call
        // if you resend the same request
        // that's why we check we empty toolCalls array because in this case
        // there will be tool calls but no content
        const isContentEmpty = !getHasUserFacingContent({
          output: content,
          toolCalls: [],
        });
        const shouldRetry = isContentEmpty && !isLastRetry;

        if (!shouldRetry) {
          messagesInput.push({
            authorType: "assistant",
            content,
            usageMetricsId,
            metadata: {
              fieldType,
              userInputFlagged,
              model,
              temperature,
              intentUsageMetricsId,
              metadata,
              language: languageCode || "en",
              isDraftTaskMessage,
              ..._.pick(promptMetadata, [
                "options",
                "showActions",
                "selectedOption",
              ]),
            },
            parentMessageId,
          });
        }

        await dispatch(
          createBulkAiMessages({
            conversationId: aiConversationId,
            messages: messagesInput,
          })
        );

        return shouldRetry
          ? { isSuccess: false, parentMessageId, hasMultiToolCallsIssue }
          : { isSuccess: true };
      }
    } catch (e) {
      setCurrentAIState("");
      setSendButtonParams({});

      // do not retry if something went wrong
      // because it might go wrong endlessly
      return { isSuccess: true };
    }
  };
};

const NON_TEMPLATE_FIELDS = ["grades", "subjects", "attachments"];

export const getUnitPlanAIContextFieldsWithValue = params => {
  return (dispatch, getState) => {
    const {
      t,
      context,
      unitPlanTemplate,
      curriculumProgramType,
      templateId,
      conversationAttachments,
      selectedAction,
    } = params;

    const selectedActionClone = _.cloneDeep(selectedAction);
    const promptMetadata = _.get(selectedActionClone, "metadata", {});

    const promptFields = getPromptFieldsFromMetadata(promptMetadata);
    const promptFieldUids = _.map(promptFields, "fieldUid");

    const state = getState();
    const academicYearId = getSelectedAcademicYearId(state);

    const learningCourseId = getSelectedLearningCourseId(state);
    const learningCourseVariables = getLearningCourseVariables({
      learningCourseId,
      academicYearId,
    });
    let learningCourse = getLearningCourseDetailsFromCache(
      learningCourseVariables
    );

    const courseId = _.get(state, "teacher.selected_class.selected_course");
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const userInfo = getUserInfo();

    const { centralRepoInput } = getUnitPlanCentralRepoInputAndFilters({
      organizationId: userInfo.org_id,
      courseId,
      academicYearId,
    });
    const responseFilter = getUnitPlanFieldResponseFilter();
    const collaboratorsFilter = {
      userAcademicYearId: academicYearId,
    };

    // reading from cache and not extracting from params
    // because unitPlanDetails in params does not updated as often
    const unitPlanDetails = getUnitPlanDetailsFromCache({
      unitPlanId,
      centralRepoInput,
      responseFilter,
      collaboratorsFilter,
      exceptionalCollaboratorsFilter: collaboratorsFilter,
    });

    // on school library route, learning course is not available in state
    const isCourseBasedGrading = getIsCourseBasedGradingEnabled();

    if (isCourseBasedGrading && isSchoolLibraryRoute()) {
      learningCourse = unitPlanDetails?.learningCourse;
    }

    const plannerElementSets = _.get(
      state,
      "platform.currentPlannerElementSets",
      {}
    );

    const plannerElementLabelCommonArgs = {
      t,
      templateId,
      curriculumType: curriculumProgramType,
      plannerElementSets,
    };

    const unitPlanFieldList = _.get(unitPlanTemplate, "field_list", {});
    const unitPlanStepList = _.get(unitPlanTemplate, "step_list", []);

    const isInterdisciplinaryUnit = getIsUnitInterdisciplinary({
      unitPlanDetails,
    });

    const isLearningCourseEnabled = getIsLearningCoursesEnabled();

    // curriculum default context fields
    const defaultContextFields = getDefaultContextFields({
      t,
      curriculumProgramType,
      isInterdisciplinaryUnit,
      isLearningCourseEnabled,
    });

    let resultFields = defaultContextFields;

    const selectedContext = _.get(state, "unitPlans.ai.selectedContext", {});
    const isUnitFlow = selectedContext === "UNIT_FLOW";

    if (isUnitFlow && !getIsLearningCoursesEnabled()) {
      resultFields = [
        {
          field: "dateDuration",
          type: "UNIT",
          config: {
            label: "common:duration",
            isLocalised: true,
          },
          isRequired: true,
        },
        ...resultFields,
      ];
    }

    // check if field exists in template
    resultFields = _.filter(resultFields, resultField => {
      const { field } = resultField || {};

      const isFieldTemplateDependent = !_.includes(NON_TEMPLATE_FIELDS, field);

      if (isFieldTemplateDependent) {
        const isFieldPresentInTemplate = _.has(unitPlanFieldList, field);
        return isFieldPresentInTemplate;
      }

      return true;
    });

    // reorder fields based on required prompt fields
    resultFields = _.orderBy(resultFields, resultField => {
      const { field, isRequired } = resultField || {};
      if (isRequired) return 0;

      const isRequiredByPrompt = _.includes(promptFieldUids, field);
      return isRequiredByPrompt ? 0 : 1;
    });

    resultFields = _.map(resultFields, resultField => {
      const { config, isRequired: _isRequired, field, type } =
        resultField || {};
      const { label: configLabel, isLocalised } = config || {};

      const isRequiredByPrompt = _.includes(promptFieldUids, field);
      const isRequired = _isRequired || isRequiredByPrompt;

      const fieldList = unitPlanFieldList;

      const fieldConfig = _.get(fieldList, `${field}.config`);
      const { viewLabel, viewLabelLocale } = fieldConfig ?? {};
      let { label: fieldLabel } = fieldConfig || {};

      // take field label from step if not present in field config
      if (_.isEmpty(fieldLabel)) {
        const { step: stepId } = getHierarchyOfField({
          fieldUID: field,
          templateBody: unitPlanTemplate,
        });

        const stepObj = _.get(unitPlanStepList, stepId, {});
        const stepLabel = _.get(stepObj, "label", "");

        if (!_.isEmpty(stepLabel)) {
          fieldLabel = stepLabel;
        }
      }

      const plannerElementType = _.get(
        fieldConfig,
        "plannerElementConfig.type"
      );

      const plannerElementLabel = plannerElementType
        ? getPlannerElementLabel({
            ...plannerElementLabelCommonArgs,
            field,
            viewLabel,
            viewLabelLocale,
            plannerElementType,
          })
        : null;

      let value = null,
        tooltip = null;

      const { value: fieldValue, tooltip: fieldTooltip } = getContextFieldValue(
        {
          field,
          type,
          ...params,
          unitPlanDetails,
          learningCourse,
          context,
        }
      );

      value = fieldValue;
      tooltip = fieldTooltip;

      const isDisabled = resultField.isDisabled || config?.isDisabled;

      const label = fieldLabel ?? plannerElementLabel ?? viewLabel;
      const localisedConfigLabel = isLocalised ? t(configLabel) : configLabel;

      return {
        field,
        type,
        label: label ?? localisedConfigLabel,
        value,
        tooltip,
        isRequired,
        config,
        isDisabled,
      };
    });

    // push attachments at the end
    if (!_.isEmpty(conversationAttachments)) {
      resultFields.push({
        field: "attachments",
        type: "ATTACHMENTS",
        label: t("common:attachments"),
        value: conversationAttachments,
      });
    }

    return resultFields;
  };
};

export const clearChatConversation = params => {
  return async dispatch => {
    try {
      const aiConversationId = _.get(params, "input.id");

      const conversation = getAIConversationsFromCache({
        id: aiConversationId,
      });

      const updatedConversation = update(conversation, {
        messages: {
          edges: {
            $set: [],
          },
          totalCount: {
            $set: 0,
          },
        },
      });

      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: updatedConversation,
        },
      });

      await dispatch(resetAIConversation(params));
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const deleteMessage = params => {
  return async dispatch => {
    const { messageId, aiConversationId } = params || {};
    try {
      const conversation = getAIConversationsFromCache({
        id: aiConversationId,
      });

      // reverse the messages to get the latest message at the end
      const messages = _.reverse(
        _.map(_.get(conversation, "messages.edges", []), "node")
      );
      const messageIdx = _.findIndex(messages, { id: messageId });

      // delete all messages after the message
      const messagesToDelete = _.slice(messages, messageIdx);
      const messagesToDeleteIds = _.map(messagesToDelete, "id");

      // filter out messages that are not to be deleted
      const filteredMessages = _.filter(
        _.get(conversation, "messages.edges", []),
        edge => !_.includes(messagesToDeleteIds, _.get(edge, "node.id"))
      );

      // update the conversation in cache
      const updatedConversation = update(conversation, {
        messages: {
          edges: {
            $set: filteredMessages,
          },
          totalCount: {
            $set: _.size(filteredMessages),
          },
        },
      });

      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: updatedConversation,
        },
      });

      return await client.mutate({
        mutation: deleteAIMessageMutation,
        variables: { input: { ids: messagesToDeleteIds } },
      });
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const deleteLastMessage = params => {
  return async dispatch => {
    const { aiConversationId } = params || {};
    try {
      const conversation = getAIConversationsFromCache({
        id: aiConversationId,
      });

      // reverse the messages to get the latest message at the end
      const messages = _.reverse(
        _.map(_.get(conversation, "messages.edges", []), "node")
      );

      const lastMessage = _.last(messages);
      const lastMessageId = lastMessage?.id;

      // only delete if it is a temporary message
      if (!_.startsWith(lastMessageId, "NEW_")) return;

      const filteredMessages = _.filter(
        _.get(conversation, "messages.edges", []),
        edge => !_.isEqual(lastMessageId, _.get(edge, "node.id"))
      );

      // update the conversation in cache
      const updatedConversation = update(conversation, {
        messages: {
          edges: {
            $set: filteredMessages,
          },
          totalCount: {
            $set: _.size(filteredMessages),
          },
        },
      });

      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: updatedConversation,
        },
      });
    } catch (error) {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};
